import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  IconButton,
  MenuItem,
  TextField,
} from "@mui/material";
import { Delete, Edit } from "@mui/icons-material";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import { useDispatch } from "react-redux";

import { callBackendApi } from "../../methods/axiosInstances/backendInstance/apiRequest";
import { showSnackbar } from "../../store/snackbarSlice/snackbarSlice";

const buildInitialState = (fields) =>
  fields.reduce((acc, field) => {
    if (field.defaultValue !== undefined) {
      acc[field.name] = field.defaultValue;
      return acc;
    }

    if (field.type === "checkbox") {
      acc[field.name] = false;
      return acc;
    }

    acc[field.name] = "";
    return acc;
  }, {});

const LookupCrud = ({
  controllerUrl,
  createLabelButton,
  createLabel,
  updateLabel,
  fields,
  columns,
}) => {
  const dispatch = useDispatch();

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [open, setOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [form, setForm] = useState(buildInitialState(fields));
  const [errors, setErrors] = useState({});
  const [requestError, setRequestError] = useState("");

  const loadData = async () => {
    setLoading(true);

    const response = await callBackendApi(controllerUrl, "GET");

    if (!response.success) {
      setRequestError(response.error || "Failed to load data.");
      dispatch(showSnackbar({ message: "Could not load lookup records." }));
      setLoading(false);
      return;
    }

    setRows(Array.isArray(response.data) ? response.data : []);
    setRequestError("");
    setLoading(false);
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [controllerUrl]);

  const openCreateModal = () => {
    setSelectedRow(null);
    setErrors({});
    setRequestError("");
    setForm(buildInitialState(fields));
    setOpen(true);
  };

  const openEditModal = (rowId) => {
    const found = rows.find((row) => row.id === rowId);

    if (!found) {
      dispatch(showSnackbar({ message: "Could not find item." }));
      return;
    }

    const nextForm = fields.reduce((acc, field) => {
      const rowValue = found[field.name];

      if (rowValue === undefined || rowValue === null) {
        acc[field.name] =
          field.defaultValue !== undefined
            ? field.defaultValue
            : field.type === "checkbox"
            ? false
            : "";
        return acc;
      }

      acc[field.name] = rowValue;
      return acc;
    }, {});

    setSelectedRow(found);
    setErrors({});
    setRequestError("");
    setForm(nextForm);
    setOpen(true);
  };

  const closeModal = () => {
    setOpen(false);
    setSelectedRow(null);
    setErrors({});
    setRequestError("");
    setForm(buildInitialState(fields));
  };

  const buildPayload = () =>
    fields.reduce((acc, field) => {
      let value = form[field.name];

      if (field.type === "number" && value !== "" && value !== null) {
        value = Number(value);
      }

      if (field.type === "checkbox") {
        value = Boolean(value);
      }

      if (value === "") {
        value = null;
      }

      acc[field.name] = value;
      return acc;
    }, {});

  const saveRow = async () => {
    setSaving(true);
    setErrors({});
    setRequestError("");

    const url = selectedRow ? `${controllerUrl}/${selectedRow.id}` : controllerUrl;
    const method = selectedRow ? "PUT" : "POST";

    const response = await callBackendApi(url, method, buildPayload());

    if (!response.success) {
      if (response.validationErrors) {
        setErrors(response.validationErrors);
      }

      setRequestError(response.error || "Failed to save data.");
      dispatch(showSnackbar({ message: "Please fix the form errors." }));
      setSaving(false);
      return;
    }

    dispatch(showSnackbar({ message: "Saved successfully!" }));
    closeModal();
    setSaving(false);
    loadData();
  };

  const removeRow = async (rowId) => {
    const confirmDelete = window.confirm("Are you sure you want to delete this?");
    if (!confirmDelete) {
      return;
    }

    const response = await callBackendApi(`${controllerUrl}/${rowId}`, "DELETE");

    if (!response.success) {
      dispatch(showSnackbar({ message: "Something went wrong while deleting." }));
      return;
    }

    dispatch(showSnackbar({ message: "Deleted successfully!" }));
    loadData();
  };

  const effectiveColumns = useMemo(() => {
    if (columns && columns.length > 0) {
      return [
        ...columns,
        {
          field: "actions",
          headerName: "Actions",
          width: 120,
          sortable: false,
          filterable: false,
          renderCell: (params) => (
            <Box sx={{ display: "flex", gap: 1 }}>
              <IconButton onClick={() => openEditModal(params.row.id)} color="primary">
                <Edit />
              </IconButton>
              <IconButton onClick={() => removeRow(params.row.id)} color="error">
                <Delete />
              </IconButton>
            </Box>
          ),
        },
      ];
    }

    const generated = fields.map((field) => ({
      field: field.name,
      headerName: field.gridLabel || field.label,
      flex: field.flex || 1,
      width: field.width,
      valueGetter: (value, row) => {
        const resolvedValue =
          row && typeof row === "object"
            ? row[field.name]
            : value && typeof value === "object" && value.row
            ? value.row[field.name]
            : value;

        if (field.type === "checkbox") {
          return resolvedValue ? "Yes" : "No";
        }

        if (field.options && resolvedValue !== null && resolvedValue !== undefined) {
          const match = field.options.find((option) => option.value === resolvedValue);
          return match?.label || resolvedValue;
        }

        return resolvedValue;
      },
    }));

    return [
      { field: "id", headerName: "Id", width: 120 },
      ...generated,
      {
        field: "actions",
        headerName: "Actions",
        width: 120,
        sortable: false,
        filterable: false,
        renderCell: (params) => (
          <Box sx={{ display: "flex", gap: 1 }}>
            <IconButton onClick={() => openEditModal(params.row.id)} color="primary">
              <Edit />
            </IconButton>
            <IconButton onClick={() => removeRow(params.row.id)} color="error">
              <Delete />
            </IconButton>
          </Box>
        ),
      },
    ];
  }, [columns, fields]);

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ width: "100%", maxWidth: "1700px", margin: "auto" }}>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Button variant="contained" onClick={openCreateModal}>
            {createLabelButton}
          </Button>
        </Grid>

        <Grid item xs={12}>
          <DataGrid
            rows={rows}
            columns={effectiveColumns}
            pageSize={10}
            getRowId={(row) => row.id}
            slots={{ toolbar: GridToolbar }}
            autoHeight
          />
        </Grid>
      </Grid>

      <Dialog open={open} onClose={closeModal} fullWidth maxWidth="sm">
        <DialogTitle>{selectedRow ? updateLabel : createLabel}</DialogTitle>

        <DialogContent>
          {requestError ? (
            <Alert severity="error" sx={{ mt: 1 }}>
              {requestError}
            </Alert>
          ) : null}

          {fields.map((field) => {
            if (field.type === "checkbox") {
              return (
                <FormControlLabel
                  key={field.name}
                  sx={{ mt: 1 }}
                  control={
                    <Checkbox
                      checked={Boolean(form[field.name])}
                      onChange={(event) =>
                        setForm((prev) => ({ ...prev, [field.name]: event.target.checked }))
                      }
                    />
                  }
                  label={field.label}
                />
              );
            }

            const helperText = errors[field.name] ? errors[field.name][0] : field.helperText || "";

            return (
              <TextField
                key={field.name}
                fullWidth
                margin="normal"
                select={Boolean(field.options)}
                label={field.label}
                type={field.type || "text"}
                value={form[field.name] ?? ""}
                onChange={(event) =>
                  setForm((prev) => ({ ...prev, [field.name]: event.target.value }))
                }
                error={Boolean(errors[field.name])}
                helperText={helperText}
              >
                {field.options
                  ? field.options.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))
                  : null}
              </TextField>
            );
          })}
        </DialogContent>

        <DialogActions>
          <Button onClick={closeModal} disabled={saving}>
            Cancel
          </Button>
          <Button variant="contained" onClick={saveRow} disabled={saving}>
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default LookupCrud;
